"use client";

import { useAnalytics } from "@analytics/provider/plausible";
import { usePurchases } from "@saas/payments/hooks/purchases";
import {
	useChangeSubscriptionPlanMutation,
	useCreateCheckoutLinkMutation,
} from "@saas/payments/lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@ui/components/button";
import { useState } from "react";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { purchasesQueryKey } from "../lib/api";

interface ChangePlanButtonProps {
	userId?: string;
	organizationId?: string;
	productId: string;
	className?: string;
}

export function ChangePlanButton({
	userId,
	organizationId,
	productId,
	className,
}: ChangePlanButtonProps) {
	const { purchases, activePlan } = usePurchases(organizationId);
	const [isLoading, setIsLoading] = useState(false);
	const queryClient = useQueryClient();
	const t = useTranslations();

	const changeSubscriptionPlanMutation = useChangeSubscriptionPlanMutation();
	const createCheckoutLinkMutation = useCreateCheckoutLinkMutation();
	const { trackEvent } = useAnalytics();
	// const createCustomerPortalMutation = useCreateCustomerPortalLinkMutation();

	// 确保purchases是数组
	const purchasesArray = Array.isArray(purchases) ? purchases : [];

	// Find the active subscription
	const activeSubscription = purchasesArray.find(
		(purchase: {
			type: string;
			status: string | null;
			subscriptionId: string | null;
			purchaseId?: string;
		}) =>
			purchase.type === "SUBSCRIPTION" &&
			purchase.status === "active" &&
			purchase.subscriptionId,
	);

	// Find a cancelled subscription
	const cancelledSubscription = purchasesArray.find(
		(purchase: {
			type: string;
			status: string | null;
			subscriptionId: string | null;
		}) =>
			purchase.type === "SUBSCRIPTION" &&
			purchase.status === "cancelled" &&
			purchase.subscriptionId,
	);

	const handleChangePlan = async () => {
		// Check if productId is valid
		if (!productId || productId.trim() === "") {
			toast.error("Invalid plan selected");
			return;
		}

		if (!(userId || organizationId)) {
			return;
		}

		setIsLoading(true);

		try {
			// 如果用户已有活跃订阅，直接调用change subscription plan API
			if (activeSubscription?.subscriptionId && activePlan?.purchaseId) {
				console.log("Changing subscription plan via API");
				console.log(
					"subscriptionId:",
					activeSubscription.subscriptionId,
				);
				console.log("newProductId:", productId);

				// 直接调用计划变更API
				const response =
					await changeSubscriptionPlanMutation.mutateAsync({
						subscriptionId: activeSubscription.subscriptionId,
						newProductId: productId,
						immediately: false,
					});

				// 刷新购买数据以更新UI
				queryClient.invalidateQueries({
					queryKey: purchasesQueryKey(organizationId),
				});

				toast.success("Subscription plan changed successfully");
			} else {
				// 如果用户没有活跃订阅，创建新的 checkout
				// 使用干净的URL作为redirectUrl
				const cleanRedirectUrl = `${window.location.origin}/app/subscription`;
				const { checkoutLink } =
					await createCheckoutLinkMutation.mutateAsync({
						type: "subscription",
						productId,
						organizationId,
						redirectUrl: cleanRedirectUrl,
					});

				// Track checkout click event if it's a creem payment link
				if (checkoutLink && checkoutLink.includes("creem")) {
					trackEvent("Checkout Clicked", {
						productId: productId,
					});
				}

				window.location.href = checkoutLink;
			}
		} catch (error) {
			console.error("Change plan error:", error);
			toast.error("Failed to change plan");
		} finally {
			setIsLoading(false);
		}
	};

	// Disable the button if productId is not valid or if it's the same plan or if subscription is cancelled
	const isProductIdValid = productId && productId.trim() !== "";

	// 修复 isSamePlan 逻辑，确保只有当前计划被禁用
	// 使用 activePlan.price?.productId 与当前 productId 比较
	// 如果产品ID相同，说明是同一个产品，按钮应该禁用
	// 如果产品ID不同，即使是同一个计划的不同周期（月度/年度），按钮也应该可点击
	const isSamePlan = activePlan?.price?.productId === productId;

	// 添加调试日志
	console.log("ChangePlanButton debug info:", {
		productId,
		activeProductId: activePlan?.price?.productId,
		isSamePlan,
		isProductIdValid,
		isSubscriptionCancelled: Boolean(cancelledSubscription),
	});

	const isSubscriptionCancelled = Boolean(cancelledSubscription);

	// Determine the button text based on whether user has an active subscription
	const buttonText = activeSubscription?.subscriptionId
		? t("settings.billing.changePlan.title")
		: t("pricing.choosePlan");

	// Tooltip text to explain why button is disabled
	let tooltipText = "";
	if (isSubscriptionCancelled) {
		tooltipText = "Please resume your subscription before changing plans";
	} else if (isSamePlan) {
		tooltipText = "You are already subscribed to this plan";
	}

	return (
		<Button
			onClick={handleChangePlan}
			disabled={
				isLoading ||
				!isProductIdValid ||
				isSamePlan ||
				isSubscriptionCancelled
			}
			className={className}
			variant="outline"
			title={tooltipText}
		>
			{isLoading ? "Processing..." : buttonText}
		</Button>
	);
}
